/*
 * Toastr (TypeScript Port)
 * Originally by John Papa, Hans Fjällemark, and Tim Ferrell.
 * jQuery dependency removed — rewritten in modern ES6+ TypeScript.
 * MIT License: http://www.opensource.org/licenses/mit-license.php
 */

function animate(el: ToastElement, method: AnimateMethod, opts: AnimateOptions = {}): void {
  const duration = opts.duration ?? 300;
  const timeoutDuration = duration - 30;
  const complete = opts.complete ?? (() => {});

  if (method === 'fadeIn' || method === 'slideDown' || method === 'show') {
    el.style.display = '';
    el.style.transition = '';
    void el.offsetWidth; // reflow

    if (method === 'fadeIn') {
      el.style.opacity = '0';
      el.style.transition = `opacity ${duration}ms ease`;
      void el.offsetWidth;
      el.style.opacity = '1';
    } else if (method === 'slideDown') {
      el.style.overflow = 'hidden';
      el.style.height = '0px';
      el.style.transition = `height ${duration}ms ease`;
      void el.offsetWidth;
      el.style.height = `${el.scrollHeight}px`;
    } else {
      // 'show' — instant
      complete();
      return;
    }

    el._animTid = setTimeout(() => {
      el.style.transition = '';
      if (method === 'slideDown') {
        el.style.height = '';
        el.style.overflow = '';
      }
      complete();
    }, timeoutDuration);
  } else {
    // hide variants
    if (method === 'fadeOut') {
      el.style.transition = `opacity ${duration}ms ease`;
      void el.offsetWidth;
      el.style.opacity = '0';
    } else if (method === 'slideUp') {
      const height = el.scrollHeight;
      // el.style.marginTop = `${-4}px`;

      el.style.paddingTop = '0';
      el.style.paddingBottom = '0';
      el.style.overflow = 'hidden';
      el.style.height = `${height}px`;
      el.style.transition = `height ${duration}ms ease, margin-top ${duration}ms ease, padding-top ${duration}ms ease`;
      void el.offsetWidth;
      el.style.height = '0px';
    } else {
      // 'hide' — instant
      el.style.display = 'none';
      complete();
      return;
    }

    el._animTid = setTimeout(() => {
      el.style.display = 'none';
      el.style.transition = '';
      el.style.opacity = '';
      el.style.height = '';
      el.style.overflow = '';
      complete();
    }, timeoutDuration);
  }
}

function stopAndShow(el: ToastElement, method: ShowMethod, duration: number, easing?: string): void {
  if (el._animTid != null) {
    clearTimeout(el._animTid);
    el._animTid = undefined;
  }
  el.style.transition = '';
  animate(el, method, { duration, easing });
}

// ─── DOM helpers ──────────────────────────────────────────────────────────────

const addClass = (el: HTMLElement, cls: string): void => {
  cls
    .trim()
    .split(/\s+/)
    .forEach((c) => c && el.classList.add(c));
};

const removeEl = (el: HTMLElement | null): void => {
  el?.parentNode?.removeChild(el);
};

const isVisible = (el: HTMLElement): boolean => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;

const hasFocus = (el: HTMLElement): boolean => el.contains(document.activeElement) && document.activeElement !== document.body;

const parseHtml = (html: string): HTMLElement => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.firstElementChild as HTMLElement;
};

const escapeHtml = (source: string): string =>
  source.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ─── Core ─────────────────────────────────────────────────────────────────────

function createToastr(): ToastrInstance {
  let $container: HTMLElement | null = null;
  let listener: ((response: ToastResponse) => void) | null = null;
  let toastId = 0;
  let previousToast: string | undefined;

  const getDefaults = (): Required<ToastrOptions> => ({
    tapToDismiss: true,
    toastClass: 'toast',
    containerId: 'toast-container',
    debug: false,

    showMethod: 'fadeIn',
    showDuration: 300,
    showEasing: 'swing',
    onShown: undefined!,

    hideMethod: 'fadeOut',
    hideDuration: 1000,
    hideEasing: 'swing',
    onHidden: undefined!,

    closeMethod: false,
    closeDuration: false,
    closeEasing: false,
    closeButton: true,
    closeOnHover: true,
    onCloseClick: undefined!,

    extendedTimeOut: 1000,
    iconClasses: {
      error: 'toast-error',
      info: 'toast-info',
      success: 'toast-success',
      warning: 'toast-warning',
    },
    iconClass: 'toast-info',
    positionClass: 'toast-top-right',
    timeOut: 5000,
    titleClass: 'toast-title',
    messageClass: 'toast-message',
    escapeHtml: false,
    target: 'body',
    closeHtml: '<button type="button">&times;</button>',
    closeClass: 'toast-close-button',
    newestOnTop: true,
    preventDuplicates: false,
    progressBar: false,
    progressClass: 'toast-progress',
    rtl: false,
    onclick: undefined!,
  });

  const getOptions = (): Required<ToastrOptions> => ({ ...getDefaults(), ...toastr.options }) as Required<ToastrOptions>;

  const publish = (args: ToastResponse): void => listener?.(args);

  const createContainer = (options: Required<ToastrOptions>): HTMLElement => {
    const el = document.createElement('div');
    el.id = options.containerId;
    addClass(el, options.positionClass);
    const target = typeof options.target === 'string' ? (document.querySelector<HTMLElement>(options.target) ?? document.body) : options.target;
    target.appendChild(el);
    return el;
  };

  const getContainer = (opts?: ToastrOptions, create = false): HTMLElement | null => {
    const options = opts ? ({ ...getDefaults(), ...opts } as Required<ToastrOptions>) : getOptions();
    $container = document.getElementById(options.containerId);
    if (!$container && create) {
      $container = createContainer(options);
    }
    return $container;
  };

  const removeToast = (toastEl: HTMLElement): void => {
    if (!$container) $container = getContainer();
    if (isVisible(toastEl)) return;
    removeEl(toastEl);
    if ($container?.children.length === 0) {
      removeEl($container);
      $container = null;
      previousToast = undefined;
    }
  };

  const clearToast = (toastEl: HTMLElement, options: Required<ToastrOptions>, clearOpts?: ClearOptions): boolean => {
    const force = clearOpts?.force ?? false;
    if (force || !hasFocus(toastEl)) {
      animate(toastEl, options.hideMethod, {
        duration: options.hideDuration,
        easing: options.hideEasing,
        complete: () => removeToast(toastEl),
      });
      return true;
    }
    return false;
  };

  const clearContainer = (options: Required<ToastrOptions>): void => {
    const children = $container?.children ? Array.from($container.children) : [];
    [...children].reverse().forEach((child) => clearToast(child as HTMLElement, options));
  };

  const clear = (toastEl?: HTMLElement | null, clearOpts?: ClearOptions): void => {
    const options = getOptions();
    if (!$container) getContainer(options);
    if (!toastEl || !clearToast(toastEl, options, clearOpts)) {
      clearContainer(options);
    }
  };

  const remove = (toastEl?: HTMLElement | null): void => {
    const options = getOptions();
    if (!$container) getContainer(options);
    if (toastEl && !hasFocus(toastEl)) {
      removeToast(toastEl);
      return;
    }
    if ($container?.children.length) removeEl($container);
  };

  const notify = (map: NotifyMap): HTMLElement | undefined => {
    const options = getOptions();
    let iconClass = map.iconClass ?? options.iconClass;

    if (map.optionsOverride) {
      Object.assign(options, map.optionsOverride);
      iconClass = map.optionsOverride.iconClass ?? iconClass;
    }

    // Duplicate check
    if (options.preventDuplicates) {
      if (map.message === previousToast) return undefined;
      previousToast = map.message;
    }

    toastId++;
    $container = getContainer(options, true)!;

    let intervalId: ReturnType<typeof setTimeout> | null = null;
    const toastEl = document.createElement('div') as ToastElement;
    const titleEl = document.createElement('div');
    const messageEl = document.createElement('div');
    const progressEl = document.createElement('div');
    const closeEl = parseHtml(options.closeHtml);

    const progressBar = { intervalId: null as ReturnType<typeof setInterval> | null, hideEta: 0, maxHideTime: 0 };

    const response: ToastResponse = {
      toastId,
      state: 'visible',
      startTime: new Date(),
      options,
      map,
    };

    // ── aria ──
    const ariaValue = map.iconClass === 'toast-success' || map.iconClass === 'toast-info' ? 'polite' : 'assertive';
    toastEl.setAttribute('aria-live', ariaValue);

    // ── icon / classes ──
    if (map.iconClass) {
      addClass(toastEl, options.toastClass);
      addClass(toastEl, iconClass);
    }
    if (options.rtl) addClass(toastEl, 'rtl');

    // ── close button ──
    if (options.closeButton && closeEl) {
      addClass(closeEl, options.closeClass);
      closeEl.setAttribute('role', 'button');
      toastEl.prepend(closeEl);
    }

    // ── progress bar ──
    if (options.progressBar) {
      addClass(progressEl, options.progressClass);
      toastEl.prepend(progressEl);
    }

    // ── title ──
    if (map.title) {
      const content = options.escapeHtml ? escapeHtml(map.title) : map.title;
      titleEl.innerHTML = content;
      addClass(titleEl, options.titleClass);
      toastEl.append(titleEl);
    }

    // ── message ──
    if (map.message) {
      const content = options.escapeHtml ? escapeHtml(map.message) : map.message;
      messageEl.innerHTML = content;
      addClass(messageEl, options.messageClass);
      toastEl.append(messageEl);
    }

    // ── sequence ──
    options.newestOnTop ? $container.prepend(toastEl) : $container.append(toastEl);

    // ── hide logic ──
    const hideToast = (override = false): void => {
      if (hasFocus(toastEl) && !override) return;

      const method = override && options.closeMethod !== false ? options.closeMethod : options.hideMethod;
      const duration = override && options.closeDuration !== false ? options.closeDuration : options.hideDuration;

      if (progressBar.intervalId != null) clearInterval(progressBar.intervalId);

      animate(toastEl, method as HideMethod, {
        duration: duration as number,
        complete: () => {
          removeToast(toastEl);
          if (intervalId != null) clearTimeout(intervalId);
          if (options.onHidden && response.state !== 'hidden') options.onHidden();
          response.state = 'hidden';
          response.endTime = new Date();
          publish(response);
        },
      });
    };

    const delayedHideToast = (): void => {
      if (options.timeOut > 0 || options.extendedTimeOut > 0) {
        intervalId = setTimeout(hideToast, options.extendedTimeOut);
        progressBar.maxHideTime = options.extendedTimeOut;
        progressBar.hideEta = Date.now() + progressBar.maxHideTime;
      }
    };

    const stickAround = (): void => {
      if (intervalId != null) clearTimeout(intervalId);
      progressBar.hideEta = 0;
      stopAndShow(toastEl, options.showMethod, options.showDuration, options.showEasing);
    };

    const updateProgress = (): void => {
      const pct = ((progressBar.hideEta - Date.now()) / progressBar.maxHideTime) * 100;
      progressEl.style.width = `${pct}%`;
    };

    // ── events ──
    if (options.closeOnHover) {
      toastEl.addEventListener('mouseenter', stickAround);
      toastEl.addEventListener('mouseleave', delayedHideToast);
    }

    if (!options.onclick && options.tapToDismiss) {
      toastEl.addEventListener('click', () => hideToast());
    }

    if (options.closeButton && closeEl) {
      closeEl.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation();
        options.onCloseClick?.(e);
        hideToast(true);
      });
    }

    if (options.onclick) {
      toastEl.addEventListener('click', (e: MouseEvent) => {
        options.onclick!(e);
        hideToast();
      });
    }

    // ── display ──
    toastEl.style.display = 'none';
    animate(toastEl, options.showMethod, {
      duration: options.showDuration,
      easing: options.showEasing,
      complete: options.onShown,
    });

    if (options.timeOut > 0) {
      intervalId = setTimeout(hideToast, options.timeOut);
      progressBar.maxHideTime = options.timeOut;
      progressBar.hideEta = Date.now() + progressBar.maxHideTime;
      if (options.progressBar) {
        progressBar.intervalId = setInterval(updateProgress, 10);
      }
    }

    publish(response);
    if (options.debug) console.log(response);

    return toastEl;
  };

  const toastr: ToastrInstance = {
    version: '2.1.4',
    options: {},

    success: (message?, title?, opts?) =>
      notify({ type: 'success', iconClass: getOptions().iconClasses.success, message, title, optionsOverride: opts }),

    info: (message?, title?, opts?) => notify({ type: 'info', iconClass: getOptions().iconClasses.info, message, title, optionsOverride: opts }),

    warning: (message?, title?, opts?) =>
      notify({ type: 'warning', iconClass: getOptions().iconClasses.warning, message, title, optionsOverride: opts }),

    error: (message?, title?, opts?) =>
      notify({ type: 'error', iconClass: getOptions().iconClasses.error, message, title, optionsOverride: opts }),

    clear,
    remove,
    subscribe: (cb) => {
      listener = cb;
    },
    getContainer,
  };

  return toastr;
}

export const toastr = createToastr();
export default toastr;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastType = 'error' | 'info' | 'success' | 'warning';

export type ShowMethod = 'fadeIn' | 'slideDown' | 'show';
export type HideMethod = 'fadeOut' | 'slideUp' | 'hide';
export type AnimateMethod = ShowMethod | HideMethod;

export interface ToastIconClasses {
  error: string;
  info: string;
  success: string;
  warning: string;
}

export interface ToastrOptions {
  tapToDismiss?: boolean;
  toastClass?: string;
  containerId?: string;
  debug?: boolean;

  showMethod?: ShowMethod;
  showDuration?: number;
  showEasing?: string;
  onShown?: () => void;

  hideMethod?: HideMethod;
  hideDuration?: number;
  hideEasing?: string;
  onHidden?: () => void;

  closeMethod?: HideMethod | false;
  closeDuration?: number | false;
  closeEasing?: string | false;
  closeOnHover?: boolean;
  onCloseClick?: (event: MouseEvent) => void;

  extendedTimeOut?: number;
  iconClasses?: ToastIconClasses;
  iconClass?: string;
  positionClass?: string;
  timeOut?: number;
  titleClass?: string;
  messageClass?: string;
  escapeHtml?: boolean;
  target?: string | HTMLElement;
  closeHtml?: string;
  closeClass?: string;
  closeButton?: boolean;
  newestOnTop?: boolean;
  preventDuplicates?: boolean;
  progressBar?: boolean;
  progressClass?: string;
  rtl?: boolean;

  onclick?: (event: MouseEvent) => void;
}

export type ToastState = 'visible' | 'hidden';

export interface ToastResponse {
  toastId: number;
  state: ToastState;
  startTime: Date;
  endTime?: Date;
  options: Required<ToastrOptions>;
  map: NotifyMap;
}

export interface NotifyMap {
  type: ToastType;
  iconClass: string;
  message?: string;
  title?: string;
  optionsOverride?: ToastrOptions;
}

export interface ClearOptions {
  force?: boolean;
}

export interface ToastrInstance {
  readonly version: string;
  options: ToastrOptions;
  success(message?: string, title?: string, options?: ToastrOptions): HTMLElement | undefined;
  info(message?: string, title?: string, options?: ToastrOptions): HTMLElement | undefined;
  warning(message?: string, title?: string, options?: ToastrOptions): HTMLElement | undefined;
  error(message?: string, title?: string, options?: ToastrOptions): HTMLElement | undefined;
  clear(toastElement?: HTMLElement | null, clearOptions?: ClearOptions): void;
  remove(toastElement?: HTMLElement | null): void;
  subscribe(callback: (response: ToastResponse) => void): void;
  getContainer(options?: ToastrOptions, create?: boolean): HTMLElement | null;
}

// ─── Animation helper ─────────────────────────────────────────────────────────

interface AnimateOptions {
  duration?: number;
  easing?: string;
  complete?: () => void;
}

// Augment HTMLElement to carry our animation timer id
interface ToastElement extends HTMLElement {
  _animTid?: ReturnType<typeof setTimeout>;
}
